'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import {
  ArrowRight,
  Bike,
  Bus,
  Car,
  CircleCheck,
  Clock,
  LoaderCircle,
  Mail,
  Map,
  MapPin,
  Phone,
  Tag,
  TriangleAlert,
  X,
} from 'lucide-react';
import { WhatsAppIcon } from '@/components/BrandIcons';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    // Champ leurre : invisible pour un humain, rempli par les robots.
    // Sa présence côté serveur suffit à écarter l'envoi.
    website: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (submitStatus !== 'idle') {
      const timer = setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000); // Disparaît après 5 secondes
      return () => clearTimeout(timer);
    }
  }, [submitStatus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Une erreur est survenue');
      }

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        website: '',
      });
    } catch (error) {
      // Le serveur explique précisément ce qui ne va pas (champ trop court,
      // adresse invalide, trop d'envois) : on le montre au visiteur plutôt
      // qu'un message générique.
      setErrorMessage(
        error instanceof Error ? error.message : 'Une erreur est survenue'
      );
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-c2e-blue to-c2e-blue-light text-white">
        {/* ... existing hero code ... */}
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold sm:text-5xl mb-4">
              Contactez-nous
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Une question ? Besoin d'informations ? Notre équipe est à votre écoute
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Informations de contact (Kept as is, just collapsed for brevity in prompt) */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Nos coordonnées
              </h2>
              {/* ... existing coordinates code ... */}
              <div className="space-y-6">
                 {/* Adresse */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-12 h-12 bg-c2e-blue-lightest text-c2e-blue rounded-lg">
                      <MapPin className="h-6 w-6" aria-hidden="true" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Adresse</h3>
                    <p className="text-gray-600">Agoe Kititidjan<br />Lomé, Togo</p>
                  </div>
                </div>

                {/* Téléphone */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-12 h-12 bg-c2e-blue-lightest text-c2e-blue rounded-lg">
                      <Phone className="h-6 w-6" aria-hidden="true" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Téléphone</h3>
                    <a href="tel:+22870013605" className="text-c2e-blue hover:text-c2e-blue-light">
                      +228 70 01 36 05
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-12 h-12 bg-c2e-blue-lightest text-c2e-blue rounded-lg">
                      <Mail className="h-6 w-6" aria-hidden="true" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    <a href="mailto:c2ereussite@gmail.com" className="text-c2e-blue hover:text-c2e-blue-light">
                      c2ereussite@gmail.com
                    </a>
                  </div>
                </div>

                {/* Horaires */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-12 h-12 bg-c2e-blue-lightest text-c2e-blue rounded-lg">
                      <Clock className="h-6 w-6" aria-hidden="true" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Horaires d'ouverture</h3>
                    <p className="text-gray-600">
                      Lundi - Samedi : 08h00 - 18h00<br />
                      Dimanche : 14h00 - 18h00
                    </p>
                  </div>
                </div>
              </div>

              {/* WhatsApp CTA */}
              <div className="mt-8 p-6 bg-green-50 rounded-2xl border-2 border-green-200">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <WhatsAppIcon className="h-6 w-6 text-green-600" />
                  Contact rapide via WhatsApp
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Obtenez une réponse immédiate à vos questions
                </p>
                <Link
                  href="https://wa.me/22870013605"
                  target="_blank"
                  className="inline-flex items-center gap-2 rounded-full bg-green-600 px-6 py-3 text-sm font-semibold text-white hover:bg-green-700 transition-colors"
                >
                  Discuter sur WhatsApp
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Formulaire de contact */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Envoyez-nous un message
              </h2>

              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-center justify-between gap-2 animate-fadeIn relative">
                  <div className="flex items-center gap-2">
                    <CircleCheck className="h-5 w-5 shrink-0" aria-hidden="true" />
                    <span>Votre message a bien été envoyé ! Nous vous répondrons très vite.</span>
                  </div>
                  <button 
                    onClick={() => setSubmitStatus('idle')}
                    className="p-1 hover:bg-green-100 rounded-full transition-colors"
                    aria-label="Fermer"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center justify-between gap-2 animate-fadeIn relative">
                  <div className="flex items-center gap-2">
                    <TriangleAlert className="h-5 w-5 shrink-0" aria-hidden="true" />
                    <span>
                      {errorMessage ||
                        'Une erreur est survenue. Veuillez réessayer ou utiliser WhatsApp.'}
                    </span>
                  </div>
                  <button 
                    onClick={() => setSubmitStatus('idle')}
                    className="p-1 hover:bg-red-100 rounded-full transition-colors"
                    aria-label="Fermer"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Champ leurre anti-robot. Masqué à l'écran et retiré de
                    l'ordre de tabulation comme de l'arbre d'accessibilité :
                    seul un automate le remplit. */}
                <div aria-hidden="true" className="absolute left-[-9999px] w-px h-px overflow-hidden">
                  <label htmlFor="website">Ne pas remplir ce champ</label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  />
                </div>

                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    id="name"
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-c2e-blue focus:border-transparent transition-colors bg-white text-gray-900 disabled:opacity-50 disabled:bg-gray-50"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-c2e-blue focus:border-transparent transition-colors bg-white text-gray-900 disabled:opacity-50 disabled:bg-gray-50"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-c2e-blue focus:border-transparent transition-colors bg-white text-gray-900 disabled:opacity-50 disabled:bg-gray-50"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    Objet *
                  </label>
                  <select
                    id="subject"
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-c2e-blue focus:border-transparent transition-colors bg-white text-gray-900 appearance-none disabled:opacity-50 disabled:bg-gray-50"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%231e40af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 0.75rem center',
                      backgroundSize: '1.5em 1.5em',
                      paddingRight: '2.5rem'
                    }}
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  >
                    <option value="">Sélectionnez un sujet</option>
                    <option value="Inscription">Inscription</option>
                    <option value="Informations sur les cours">Informations sur les cours</option>
                    <option value="Événements">Événements</option>
                    <option value="Partenariat">Partenariat</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    required
                    rows={6}
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-c2e-blue focus:border-transparent transition-colors bg-white text-gray-900 disabled:opacity-50 disabled:bg-gray-50"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full rounded-full bg-c2e-blue px-8 py-4 text-base font-semibold text-white hover:bg-c2e-blue-light transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <LoaderCircle
                        className="animate-spin h-5 w-5 text-white"
                        aria-hidden="true"
                      />
                      Envoi en cours...
                    </>
                  ) : (
                    'Envoyer le message'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section - style Defitech (bandeau horizontal, pleine largeur, épuré) */}
      <section className="py-10 bg-c2e-gray-light">
        <h2 className="text-2xl font-bold text-center text-gray-900 mb-6">
          Notre localisation
        </h2>
        <div className="relative left-1/2 right-1/2 w-screen -translate-x-1/2" style={{ minHeight: 0, margin: 0, padding: 0 }}>
          <iframe
            src="https://www.google.com/maps?q=Agoe+Kititidjan,+Lom%C3%A9,+Togo&output=embed"
            width="100%"
            height="500"
            style={{ border: 0, display: 'block', width: '100vw', height: '500px' }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Carte d'accès C2E"
          />
        </div>
        <div className="flex flex-col items-center gap-6 mt-8 px-4">
          {/* CTA Button avec effet hover amélioré */}
          <a
            href="https://www.google.com/maps/dir/?api=1&destination=Agoe+Kititidjan,+Lom%C3%A9,+Togo"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-c2e-blue to-c2e-blue-light px-10 py-4 text-base font-bold text-white hover:shadow-lg hover:scale-105 transition-all duration-300 shadow-md"
          >
            <Map className="h-5 w-5" aria-hidden="true" />
            Obtenir l'itinéraire
          </a>

          {/* Card info avec icônes et sections colorées */}
          <div className="bg-white rounded-2xl shadow-lg p-6 max-w-3xl w-full border border-gray-100">
            {/* Adresse principale */}
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 bg-c2e-blue/10 rounded-xl flex items-center justify-center">
                <MapPin className="h-6 w-6 text-c2e-blue" aria-hidden="true" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-lg mb-1">Adresse</h3>
                <p className="text-gray-700 leading-relaxed">
                  <span className="font-semibold text-c2e-blue">Agoe Kititidjan</span>, Lomé, Togo
                </p>
              </div>
            </div>

            {/* Repères avec fond coloré */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 mb-5 border-l-4 border-c2e-blue">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Tag className="h-5 w-5 text-c2e-blue" aria-hidden="true" />
                Points de repère
              </h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <CircleCheck
                    className="h-5 w-5 text-c2e-blue mt-0.5 shrink-0"
                    aria-hidden="true"
                  />
                  <span>Non loin de la <strong>station Total Agoe</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <CircleCheck
                    className="h-5 w-5 text-c2e-blue mt-0.5 shrink-0"
                    aria-hidden="true"
                  />
                  <span>Secteur brasserie après la clôture du camp</span>
                </li>
                <li className="flex items-start gap-2">
                  <CircleCheck
                    className="h-5 w-5 text-c2e-blue mt-0.5 shrink-0"
                    aria-hidden="true"
                  />
                  <span>À côté de l'<strong>école primaire Kititidjan</strong></span>
                </li>
              </ul>
            </div>

            {/* Moyens de transport */}
            <div className="pt-4 border-t border-gray-200">
              <p className="text-sm font-semibold text-gray-700 text-center mb-3">Comment nous rejoindre ?</p>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="flex items-center gap-2 text-gray-700">
                  <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center">
                    <Bike className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-sm font-medium">Zem (taxi-moto)</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                    <Bus className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-sm font-medium">Bus</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center">
                    <Car className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-sm font-medium">Voiture</span>
                </div>
              </div>
            </div>

            {/* Call to action téléphone */}
            <div className="mt-5 pt-5 border-t border-gray-200 text-center">
              <p className="text-gray-600 text-sm mb-3">Besoin d'aide pour nous trouver ?</p>
              <a 
                href="tel:+22870013605"
                className="inline-flex items-center gap-2 text-c2e-blue font-semibold hover:text-c2e-blue-light transition-colors"
              >
                <Phone className="h-5 w-5" aria-hidden="true" />
                Appelez-nous : +228 70 01 36 05
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
